import React from 'react';
import { ReportingVolume } from '../../services/api';
import { RangePreset } from './DateRangePicker';

/**
 * The four cards above the chart, in the DASHBOARD's vocabulary:
 *
 *   Today | <selected range> | Gross | Delivery rate
 *
 * This replaced Leads In / Passed Spine / Failed / Spine Pass Rate. The old
 * four were spine-outcome words that appeared nowhere else in the product,
 * so nobody could line a report up against the dashboard without a mental
 * translation step. These four are the same figures, derived the same way,
 * as the dashboard's volume columns — see [[project_dashboard_volume_columns]].
 *
 * Today and Gross are FIXED windows. The date picker moves only the middle
 * card (and the chart, and the rate), which is why its label follows the
 * preset rather than being hardcoded to "7 Days".
 *
 * Delivery rate is a FAN-OUT RATIO, not a percentage of a whole: one
 * accepted lead delivered to three destinations is 300%. Over 100% is
 * normal and must never be clamped. Null renders as an em dash, never as
 * "0%" — no traffic is not the same as total delivery failure.
 */

export const RANGE_LABELS: Record<RangePreset, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  last7: '7 Days',
  last30: '30 Days',
  thisMonth: 'This Month',
  custom: 'Selected Range',
};

function fmt(n: number): string {
  if (n >= 1_000_000) {return (n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1) + 'M';}
  if (n >= 10_000) {return Math.round(n / 1000) + 'K';}
  return n.toLocaleString();
}

export function fmtExact(n: number): string {
  return n.toLocaleString();
}

export function fmtRate(rate: number | null): string {
  return rate === null ? '—' : (rate * 100).toFixed(rate >= 10 ? 0 : 1) + '%';
}

/** Shared colouring for a fan-out delivery rate. Matches the dashboard. */
export function deliveryRateColor(rate: number | null): string {
  if (rate === null) {return '#9ca3af';}
  if (rate >= 1) {return '#047857';}
  if (rate >= 0.6) {return '#b45309';}
  return '#b91c1c';
}

/** Colouring for a true 0..1 accept rate — a different scale on purpose. */
export function acceptRateColor(rate: number | null): string {
  if (rate === null) {return '#9ca3af';}
  if (rate >= 0.95) {return '#047857';}
  if (rate >= 0.75) {return '#b45309';}
  return '#b91c1c';
}

interface Props {
  volume: ReportingVolume;
  preset: RangePreset;
  /**
   * Label and colour of the fourth card. Inbound surfaces show the fan-out
   * "Delivery rate"; a single destination shows its own "Accept rate",
   * which is a genuinely different measurement.
   */
  rateLabel?: string;
  rateVariant?: 'delivery' | 'accept';
}

export default function VolumeCards({
  volume,
  preset,
  rateLabel = 'Delivery Rate',
  rateVariant = 'delivery',
}: Props) {
  const rateColor =
    rateVariant === 'accept' ? acceptRateColor(volume.deliveryRate) : deliveryRateColor(volume.deliveryRate);

  const cards = [
    { label: 'Today', value: fmt(volume.today), exact: fmtExact(volume.today), color: '#111827' },
    { label: RANGE_LABELS[preset], value: fmt(volume.range), exact: fmtExact(volume.range), color: '#111827' },
    { label: 'Gross', value: fmt(volume.gross), exact: fmtExact(volume.gross), color: '#111827' },
    { label: rateLabel, value: fmtRate(volume.deliveryRate), exact: undefined, color: rateColor },
  ];

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: 12,
    }}>
      {cards.map(c => (
        <div
          key={c.label}
          title={c.exact}
          style={{
            padding: '18px 20px',
            background: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
          }}
        >
          <div style={{
            fontSize: 12,
            color: '#6b7280',
            textTransform: 'uppercase',
            letterSpacing: 0.4,
            fontWeight: 600,
          }}>
            {c.label}
          </div>
          <div style={{ marginTop: 6, fontSize: 28, fontWeight: 700, color: c.color }}>
            {c.value}
          </div>
        </div>
      ))}
    </div>
  );
}
